/**
 * Avatars « monde du campus » (dessinés par Charles, apparence v3) — format
 * et contrôle de forme. Partagé client/serveur, sans Phaser (sous-chemin
 * `@campus/core/campus-avatar`, cf. piège du barrel).
 *
 * Le contrôle ici ne porte que sur la forme (clés connues, chaînes courtes) :
 * l'existence de chaque perso, couche et teinte est vérifiée par l'API avec
 * `validerAvatar()` du paquet livré. La planche est composée côté serveur,
 * au même format que Pixel Citizen (768×192, cases 48, rangées bas/gauche/
 * droite/haut) : le jeu l'affiche à l'identique.
 */

export const CAMPUS_CHOIX = ['peau', 'cheveux', 'haut', 'bas', 'chaussures'] as const;
export const CAMPUS_COUCHES = [
  'cheveux', 'barbe', 'teinteBarbe', 'chef', 'tissu', 'lunettes', 'monture', 'haut', 'bas', 'chaussures',
] as const;

export type CampusChoixKey = typeof CAMPUS_CHOIX[number];
export type CampusCoucheKey = typeof CAMPUS_COUCHES[number];

/**
 * Objet du contrat de Charles (`docs/specs/2026-10-17-avatars-serveur-design.md`
 * du dépôt monde-du-campus) : `{ perso, choix?, couches? }`. Un rôle absent de
 * `choix` ou à `origine` = couleur du dessin ; `couches` ne compte que pour
 * `perso: "sur-mesure"` (gardé, mais ignoré, pour un perso du catalogue).
 */
export interface CampusAvatar {
  perso: string;
  choix?: Partial<Record<CampusChoixKey, string>>;
  /** Avatar sur mesure ; une valeur vide = rien, ou le vêtement de la base. */
  couches?: Partial<Record<CampusCoucheKey, string>>;
}

/** Apparence stockée (`tenant_members.appearance`). `sheet` = empreinte de la planche composée. */
export interface CampusAppearance {
  v: 3;
  campus: CampusAvatar;
  sheet: string;
}

const NAME = /^[\p{L}\p{N}_-][\p{L}\p{N} _.-]{0,63}$/u;
const COUCHE = /^[\p{L}\p{N} _.#-]{0,64}$/u;

export type CampusAvatarResult = { ok: true; value: CampusAvatar } | { ok: false; error: string };

/**
 * Remet l'avatar en forme canonique (ordre des clés fixe, clés inconnues
 * refusées) : `JSON.stringify` du résultat sert d'empreinte.
 */
export function normalizeCampusAvatar(input: unknown): CampusAvatarResult {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return { ok: false, error: 'INVALID_AVATAR' };
  const raw = input as Record<string, unknown>;
  if (Object.keys(raw).some((k) => k !== 'perso' && k !== 'choix' && k !== 'couches')) return { ok: false, error: 'UNKNOWN_FIELD' };
  if (typeof raw.perso !== 'string' || !NAME.test(raw.perso)) return { ok: false, error: 'INVALID_PERSO' };

  const value: CampusAvatar = { perso: raw.perso };
  // `null` = absent : le paquet refuse `choix: null` / `couches: null`, on ne le lui transmet jamais
  if (raw.choix !== undefined && raw.choix !== null) {
    const c = raw.choix;
    if (typeof c !== 'object' || Array.isArray(c)) return { ok: false, error: 'INVALID_CHOIX' };
    const cRaw = c as Record<string, unknown>;
    if (Object.keys(cRaw).some((k) => !(CAMPUS_CHOIX as readonly string[]).includes(k))) return { ok: false, error: 'UNKNOWN_CHOIX' };
    const choix: Partial<Record<CampusChoixKey, string>> = {};
    for (const k of CAMPUS_CHOIX) {
      const v = cRaw[k];
      if (v === undefined) continue;
      if (typeof v !== 'string' || !NAME.test(v)) return { ok: false, error: `INVALID_${k.toUpperCase()}` };
      choix[k] = v;
    }
    value.choix = choix;
  }
  if (raw.couches !== undefined && raw.couches !== null) {
    const c = raw.couches;
    if (typeof c !== 'object' || Array.isArray(c)) return { ok: false, error: 'INVALID_COUCHES' };
    const cRaw = c as Record<string, unknown>;
    if (Object.keys(cRaw).some((k) => !(CAMPUS_COUCHES as readonly string[]).includes(k))) return { ok: false, error: 'UNKNOWN_COUCHE' };
    const couches: Partial<Record<CampusCoucheKey, string>> = {};
    for (const k of CAMPUS_COUCHES) {
      const v = cRaw[k];
      if (v === undefined) continue;
      if (typeof v !== 'string' || !COUCHE.test(v)) return { ok: false, error: `INVALID_COUCHE_${k.toUpperCase()}` };
      couches[k] = v;
    }
    value.couches = couches;
  }
  return { ok: true, value };
}

export const canonicalCampusAvatar = (a: CampusAvatar): string => JSON.stringify(a);

// ── Catalogue (servi par GET /avatars/campus/catalog, lu dans le paquet) ────

/** Une teinte : son nom et sa rampe (du sombre au clair) ; `color` = ton du milieu, pour la pastille. */
export interface CampusTeinte { id: string; label: string; color: string; ramp: string[] }
export interface CampusItem { id: string; label: string }
export interface CampusChef extends CampusItem {
  /** `dessus` coupe les cheveux qui dépassent, `tout` les masque, `rien` se pose par-dessus. */
  cache: 'dessus' | 'tout' | 'rien';
}
export interface CampusVetement extends CampusItem {
  /** Teinte de « haut »/« bas »/« chaussures » prise quand le rôle reste « d'origine ». */
  teinte: string;
}
export type CampusVetementKey = 'haut' | 'bas' | 'chaussures';

/** Couches du sur-mesure (`catalogue-couches.json`). */
export interface CampusSurMesure {
  cheveux: CampusItem[];
  barbes: CampusItem[];
  chefs: CampusChef[];
  lunettes: CampusItem[];
  montures: Array<CampusItem & { color: string }>;
  vetements: Record<CampusVetementKey, CampusVetement[]>;
  /** Vêtement que porte la base (valeur vide d'une couche), ex. « Veste ». */
  vetementsBase: Record<CampusVetementKey, string>;
}

export interface CampusCatalog {
  /** Persos du catalogue (hors `sur-mesure`). */
  persos: Array<{ id: string; label: string }>;
  /** Teintes proposées pour chaque rôle (`origine` toujours permis en plus). */
  teintes: Record<CampusChoixKey, CampusTeinte[]>;
  /** Absent si le paquet ne livre pas les couches. */
  surMesure?: CampusSurMesure;
}
